import { ComplexDataset } from 'services/MetricsService';
import {
  changeMargins,
  getColors,
  getCurrKeys,
  maxHeight,
  maxWidth,
  usualMargins,
} from './utils';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { filesize } from 'filesize';
import { MSDTooltip } from './MSDTooltip';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import ChartDataLabel from './ChartDataLabel';

const Root = styled.div({});

export interface BarChartProps {
  data: ComplexDataset;
  total: string | number | undefined;
  metricType: string;
  tooltipVar: string;
  tooltipFormatter?: boolean;
  heightPercent?: number;
  marginsChanges?: Record<string, number>;
  stacked?: boolean;
  filesizeFormat?: boolean;
  chartLabelOffsetRight?: number;
}

function MSDBarChart(props: BarChartProps): React.ReactElement {
  const {
    data,
    total,
    metricType,
    heightPercent,
    marginsChanges,
    stacked,
    filesizeFormat,
    tooltipVar,
    tooltipFormatter,
    chartLabelOffsetRight,
  } = props;
  const chartHeight = heightPercent ? maxHeight * heightPercent : maxHeight;
  const chartMargins = marginsChanges
    ? changeMargins(marginsChanges)
    : usualMargins;

  const allKeys: string[] | undefined = data && getCurrKeys(data);
  const colors = getColors();

  return (
    <Root>
      <ChartDataLabel
        data={total}
        name={metricType}
        offsetRight={chartLabelOffsetRight || undefined}
      />
      <BarChart
        width={maxWidth}
        height={chartHeight}
        data={data}
        margin={chartMargins}
        {...{ overflow: 'visible' }}
      >
        <XAxis dataKey="day" stroke="#093e80" minTickGap={48} />
        <YAxis
          stroke="#093e80"
          tickFormatter={filesizeFormat ? filesize : undefined}
        />
        <CartesianGrid horizontal vertical={false} strokeDasharray="3 3" />

        {stacked && allKeys ? (
          allKeys.map((key: string, idx: number) => {
            return (
              <Bar
                type="monotone"
                dataKey={key}
                key={`Bar-${idx}`}
                stroke={colors[idx % colors.length]}
                strokeWidth={0}
                fill={colors[idx % colors.length]}
                fillOpacity={1}
                maxBarSize={40}
                stackId="a"
              />
            );
          })
        ) : (
          <Bar
            type="monotone"
            dataKey="Value"
            stroke="#16adfe"
            strokeWidth={0}
            fill="#16adfe"
            fillOpacity={0.67}
            maxBarSize={40}
          />
        )}

        <Tooltip
          position={{ x: 70, y: 10 }}
          content={
            <MSDTooltip
              varName={tooltipVar}
              varFormatter={tooltipFormatter || false}
              esPluralize={false}
            />
          }
          shared={stacked}
          cursor={{ fill: 'transparent' }}
          contentStyle={{
            border: '2px solid #e0e1e2',
          }}
        />
      </BarChart>
    </Root>
  );
}

export default observer(MSDBarChart);
